import {
  IconChevronDown,
  IconFileText,
  IconMessageQuestion,
  IconSearch,
} from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { toast } from "sonner";
import { ConfirmDialog } from "#app/components/new-ui/confirm-dialog";
import { Button } from "#app/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "#app/components/ui/dialog";
import { RailTooltip } from "#app/components/ui/tooltip";
import {
  beginOpenPolicyFromSearch,
  usePolicyListSidebar,
} from "#app/hooks/use-policy-list-sidebar";
import { cn } from "#app/utils/misc.tsx";
import {
  deletePolicyRule,
  type PolicyListRule,
  policyIsDraft,
} from "#app/utils/policy-list";

import { ChatListItem } from "./chat-list-item";

type RecentPoliciesProps = {
  onNavigate: (to: string) => void;
  collapsed: boolean;
};

/**
 * Policy list in the global sidebar Recent slot — same placement as workflow
 * chat threads on `/u/alpha/agent`. Search opens the dialog; the expanded list
 * paginates like Recent chats.
 */
export function RecentPolicies({ onNavigate, collapsed }: RecentPoliciesProps) {
  const { t } = useTranslation(["nav", "policies", "common"]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const {
    rules,
    search,
    setSearch,
    loading,
    listError,
    load,
    loadMore,
    loadingMore,
    hasMore,
    selectedId,
    isCreating,
  } = usePolicyListSidebar();

  const searchLabel = t("nav:searchPoliciesTitle");
  const needsInputLabel = t("nav:policyNeedsInput");
  const draftLabel = t("nav:policyDraft");

  const selectedRule = useMemo(
    () => rules.find((rule) => rule.publicId === selectedId) ?? null,
    [rules, selectedId],
  );

  const openSearchDialog = useCallback(() => {
    setSearch("");
    setSearchOpen(true);
  }, [setSearch]);

  const handleSearchOpenChange = useCallback(
    (open: boolean) => {
      setSearchOpen(open);
      if (!open) setSearch("");
    },
    [setSearch],
  );

  const selectPolicy = (id: string) => {
    // Drop any search URL write already queued. Clearing the box arms another
    // one; that write must reload the list and must not replace `?policy=`.
    beginOpenPolicyFromSearch(id);
    onNavigate(`/u/policies?policy=${encodeURIComponent(id)}`);
    setSearchOpen(false);
    setSearch("");
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await deletePolicyRule(deleteTarget.id);
      toast.success(`"${deleteTarget.name}" deleted.`);
      void load(search);
      if (deleteTarget.id === selectedId) {
        // Clear selection — do not force create mode.
        onNavigate("/u/policies");
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete policy",
      );
    } finally {
      setDeletingId(null);
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  // Trailing marks only. Published status lives on the library cards
  // (Published vN) — no green check here. A draft says it is one (spec:
  // lists show which policies are drafts); needs-input is the actionable cue.
  const policyStatusIcon = (rule: PolicyListRule) => {
    const draft = policyIsDraft(rule);
    if (!rule.awaitingInput && !draft) return undefined;
    return (
      <span className="flex items-center gap-1">
        {draft ? (
          <span
            className="rounded-full border border-zinc-200 px-1.5 text-[10px] leading-4 text-zinc-600 dark:border-zinc-700 dark:text-zinc-400"
            data-testid="policy-draft-badge"
          >
            {draftLabel}
          </span>
        ) : null}
        {rule.awaitingInput ? (
          <IconMessageQuestion
            size={14}
            className="text-amber-600 dark:text-amber-400"
            aria-label={needsInputLabel}
            title={needsInputLabel}
          />
        ) : null}
      </span>
    );
  };

  const renderList = () => {
    if (rules.length > 0) {
      return rules.map((rule) => (
        <ChatListItem
          key={rule.publicId}
          title={rule.name ?? "Untitled policy"}
          isActive={!isCreating && rule.publicId === selectedId}
          isDeleting={rule.publicId === deletingId}
          badge={policyStatusIcon(rule)}
          onSelect={() => selectPolicy(rule.publicId)}
          onDelete={() =>
            handleDelete(rule.publicId, rule.name ?? rule.publicId)
          }
          deleteLabel={t("common:delete")}
          moreLabel={t("common:ui.moreActions")}
        />
      ));
    }
    if (loading) {
      return (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          {t("common:loading")}
        </p>
      );
    }
    if (listError) {
      return (
        <div className="space-y-2 px-3 py-2">
          <p className="text-sm text-muted-foreground">{listError}</p>
          <Button variant="secondary" size="xs" onClick={() => void load()}>
            {t("nav:retryLoadChats")}
          </Button>
        </div>
      );
    }
    return (
      <p className="px-3 py-2 text-sm text-muted-foreground">
        {t("nav:noPolicies")}
      </p>
    );
  };

  const deleteDialog = (
    <ConfirmDialog
      open={deleteOpen}
      onOpenChange={(open) => {
        if (!open) {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }
      }}
      destructive
      title={t("common:delete")}
      description={t("policies:confirmDeletePolicy", {
        defaultValue: `Delete "${deleteTarget?.name ?? ""}"? This cannot be undone.`,
        name: deleteTarget?.name ?? "",
      })}
      confirmText={t("common:delete")}
      cancelText={t("common:cancel")}
      onConfirm={() => void confirmDelete()}
      onCancel={() => {
        setDeleteOpen(false);
        setDeleteTarget(null);
      }}
    />
  );

  const searchDialog = (
    <Dialog open={searchOpen} onOpenChange={handleSearchOpenChange}>
      <DialogContent
        showCloseButton
        className="gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        <div className="border-b px-5 py-3">
          {/* Hidden, not removed: `DialogTitle` is what gives the dialog its
              accessible name, so commenting it out left a screen reader
              announcing an unnamed dialog. `sr-only` keeps the heading off the
              screen -- which is the visual change intended here -- while the
              name survives. Same pattern as settings-modal.tsx. */}
          <DialogTitle className="sr-only">{searchLabel}</DialogTitle>
          <div className="flex h-10 items-center gap-3">
            <IconSearch className="h-5 w-5 text-zinc-500" />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("nav:searchPolicies")}
              aria-label={searchLabel}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
            />
          </div>
        </div>
        <PolicyScrollList
          className="h-115 px-3 py-4 pr-5"
          hasMore={hasMore}
          isLoading={loading || loadingMore}
          itemCount={rules.length}
          loadMore={loadMore}
          loadingLabel={t("common:loading")}
        >
          <div className="space-y-1">{renderList()}</div>
        </PolicyScrollList>
      </DialogContent>
    </Dialog>
  );

  if (collapsed) {
    const openTitle = selectedRule
      ? (selectedRule.name ?? t("nav:untitledPolicy"))
      : null;
    return (
      <div className="flex flex-col items-center gap-1">
        {selectedRule && !isCreating ? (
          <RailTooltip title={openTitle ?? t("nav:untitledPolicy")}>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => selectPolicy(selectedRule.publicId)}
              aria-label={t("nav:openPolicy", {
                name: openTitle ?? selectedRule.publicId,
              })}
              aria-current="true"
              className="-mx-2 rounded-full bg-zinc-200/60 dark:bg-zinc-800"
            >
              {selectedRule.awaitingInput ? (
                <IconMessageQuestion className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              ) : (
                <IconFileText className="h-5 w-5" />
              )}
            </Button>
          </RailTooltip>
        ) : null}
        <RailTooltip title={searchLabel}>
          <Button
            variant="ghost"
            size="icon"
            onClick={openSearchDialog}
            aria-label={searchLabel}
            className="-mx-2"
          >
            <IconSearch className="h-5 w-5" />
          </Button>
        </RailTooltip>
        {searchDialog}
        {deleteDialog}
      </div>
    );
  }

  return (
    <div className="recent-block -mx-2 -mr-3 mt-5">
      <div className="flex items-center justify-between pr-5">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="ml-3 flex cursor-pointer items-center gap-1 text-[13px] text-zinc-500/80 hover:text-black dark:hover:text-zinc-100"
        >
          {t("nav:recentPolicies")}
          <motion.span
            animate={{ rotate: expanded ? 0 : -90 }}
            transition={{ duration: 0.2 }}
            className="inline-flex"
          >
            <IconChevronDown size={18} />
          </motion.span>
        </button>

        <Button
          variant="secondary"
          size="xs"
          onClick={openSearchDialog}
          aria-label={searchLabel}
        >
          <IconSearch />
          {t("common:search")}
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <PolicyScrollList
              className="mt-3 h-[calc(100dvh-340px)] pr-3"
              hasMore={hasMore}
              isLoading={loading || loadingMore}
              itemCount={rules.length}
              loadMore={loadMore}
              loadingLabel={t("common:loading")}
            >
              <div className="space-y-1">{renderList()}</div>
            </PolicyScrollList>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {searchDialog}
      {deleteDialog}
    </div>
  );
}

type PolicyScrollListProps = {
  children: React.ReactNode;
  className?: string;
  hasMore: boolean;
  isLoading: boolean;
  itemCount: number;
  loadMore: () => void;
  loadingLabel: string;
};

function PolicyScrollList({
  children,
  className,
  hasMore,
  isLoading,
  itemCount,
  loadMore,
  loadingLabel,
}: PolicyScrollListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const tryLoadMore = useCallback(() => {
    if (hasMore && !isLoading) loadMore();
  }, [hasMore, isLoading, loadMore]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 120) tryLoadMore();
  }, [tryLoadMore]);

  useLayoutEffect(() => {
    const sentinel = endRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) tryLoadMore();
      },
      { root, rootMargin: "0px 0px 80px 0px", threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, itemCount, tryLoadMore]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={cn("overflow-y-auto overscroll-contain", className)}
    >
      {children}
      {hasMore ? (
        <div
          ref={endRef}
          className="py-2 text-center text-xs text-muted-foreground"
        >
          {isLoading && itemCount > 0 ? loadingLabel : null}
        </div>
      ) : null}
    </div>
  );
}

export default RecentPolicies;
